import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  DollarSign,
  Coins,
  Package,
  Building2,
  EyeOff,
  CheckCircle2,
  Lock,
  Sparkles,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminFinancialPanel — admin-only dashboard of every financial setting
 * currently active OR drafted. Read-only. Nothing here is exposed to
 * end users; the corporate / subscription block in particular is a
 * draft to be refined and shipped later.
 */
export default function AdminFinancialPanel({ token }) {
  const [pricing, setPricing] = useState({});
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [pRes, kRes] = await Promise.all([
          axios.get(`${API}/billing/pricing`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API}/billing/packs`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        if (cancelled) return;
        setPricing(pRes.data.pricing || {});
        setPacks(kRes.data.packs || []);
      } catch (_) {
        /* fail silently — admin page still useful without it */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const featureLabels = {
    snapshot: 'Snapshot Analysis',
    growth_model: 'Growth vs Model',
    growth_prognosis: 'Growth Prognosis',
    spreadsheet_upload: 'Spreadsheet Upload',
    spreadsheet_reanalyze: 'Spreadsheet Reanalyze',
    sfr_calculator: 'SFR Calculator',
    wingman: 'Wingman AI Chat',
  };

  // Draft corporate plans — NOT exposed anywhere user-facing yet.
  const draftPlans = [
    {
      name: 'Starter',
      price: 149,
      seats: 'up to 3',
      features: 'All analyses, all species, email support',
      tone: 'border-slate-200 bg-slate-50',
    },
    {
      name: 'Professional',
      price: 499,
      seats: 'up to 15',
      features: 'Everything in Starter + priority support, multi-cage rollups',
      tone: 'border-cyan-200 bg-cyan-50',
    },
    {
      name: 'Enterprise',
      price: 1499,
      seats: 'unlimited',
      features: 'Everything in Pro + dedicated success manager, custom SLA',
      tone: 'border-purple-200 bg-purple-50',
    },
  ];

  return (
    <Card
      className="border-slate-200 shadow-sm"
      data-testid="admin-financial-panel"
    >
      <CardHeader className="border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="flex items-center gap-2 text-slate-800">
            <DollarSign className="h-5 w-5 text-emerald-600" />
            Financial Settings
            <Badge
              variant="outline"
              className="ml-2 border-amber-300 bg-amber-50 text-amber-700 font-normal"
            >
              <EyeOff className="h-3 w-3 mr-1" />
              Admin-only · not visible to users
            </Badge>
          </CardTitle>
          <span className="text-xs text-slate-500">
            Read-only · live snapshot from MongoDB
          </span>
        </div>
      </CardHeader>

      <CardContent className="p-6 space-y-8">
        {/* ───────────────────────────── ACTIVE: Credit System ───────────────────────────── */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            <h3 className="font-semibold text-slate-800">
              Credit System (Phase 1) ·{' '}
              <span className="text-emerald-700">ACTIVE</span>
            </h3>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Pricing */}
            <div
              className="rounded-lg border border-slate-200 p-4 bg-white"
              data-testid="financial-pricing-block"
            >
              <div className="flex items-center gap-2 mb-3 text-slate-700">
                <Coins className="h-4 w-4" />
                <span className="font-semibold text-sm">
                  Per-action pricing
                </span>
              </div>
              {loading ? (
                <p className="text-sm text-slate-400">Loading…</p>
              ) : Object.keys(pricing).length === 0 ? (
                <p className="text-sm text-slate-400">No pricing configured.</p>
              ) : (
                <div className="space-y-1.5">
                  {Object.entries(pricing).map(([key, cost]) => (
                    <div
                      key={key}
                      className="flex items-center justify-between text-sm py-1 border-b border-slate-100 last:border-0"
                    >
                      <span className="text-slate-600">
                        {featureLabels[key] || key}
                      </span>
                      <span className="font-mono font-semibold text-slate-800">
                        {cost === 0 ? (
                          <span className="text-emerald-600">FREE</span>
                        ) : (
                          `${cost} cr`
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Packs */}
            <div
              className="rounded-lg border border-slate-200 p-4 bg-white"
              data-testid="financial-packs-block"
            >
              <div className="flex items-center gap-2 mb-3 text-slate-700">
                <Package className="h-4 w-4" />
                <span className="font-semibold text-sm">
                  Credit packs (Stripe Checkout)
                </span>
              </div>
              {loading ? (
                <p className="text-sm text-slate-400">Loading…</p>
              ) : packs.length === 0 ? (
                <p className="text-sm text-slate-400">No packs configured.</p>
              ) : (
                <div className="space-y-1.5">
                  {packs.map((p) => (
                    <div
                      key={p.id || p.name}
                      className="flex items-center justify-between text-sm py-1 border-b border-slate-100 last:border-0"
                    >
                      <span className="text-slate-600">
                        {p.name}
                        <span className="text-slate-400 ml-2">
                          ({Number(p.credits).toLocaleString()} cr)
                        </span>
                      </span>
                      <span className="font-mono font-semibold text-slate-800">
                        ${Number(p.price_usd).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* System flags */}
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <FlagBox label="Stripe mode" value="TEST" tone="amber" />
            <FlagBox label="Signup bonus" value="100 cr" tone="emerald" />
            <FlagBox label="Migration grant" value="500 cr · once" tone="cyan" />
            <FlagBox label="Webhook idempotency" value="ON" tone="emerald" />
          </div>
        </section>

        {/* ───────────────────────────── DRAFT: Corporate / Subscription ───────────────────────────── */}
        <section className="rounded-lg border-2 border-dashed border-amber-300 bg-amber-50/40 p-5">
          <div className="flex items-center gap-2 mb-1">
            <Lock className="h-4 w-4 text-amber-700" />
            <h3 className="font-semibold text-slate-800">
              Corporate / Subscription Model ·{' '}
              <span className="text-amber-700">DRAFT — NOT ACTIVE</span>
            </h3>
          </div>
          <p className="text-xs text-slate-600 mb-4 ml-6">
            Below are the proposed defaults captured from the design discussion.
            Nothing here is wired to the registration flow, the credit system,
            or Stripe yet. Refine the values, then ask me to ship Phase A.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            {draftPlans.map((p) => (
              <div
                key={p.name}
                className={`rounded-lg border p-4 ${p.tone}`}
                data-testid={`draft-plan-${p.name.toLowerCase()}`}
              >
                <div className="flex items-baseline justify-between mb-1">
                  <span className="font-bold text-slate-800">{p.name}</span>
                  <span className="text-lg font-bold text-slate-800">
                    ${p.price}
                    <span className="text-xs font-normal text-slate-500">
                      /mo
                    </span>
                  </span>
                </div>
                <p className="text-xs text-slate-600 mb-2">
                  Seats: <strong>{p.seats}</strong>
                </p>
                <p className="text-xs text-slate-600 leading-relaxed">
                  {p.features}
                </p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <FlagBox
              label="Registration toggle"
              value="Individual only"
              tone="slate"
            />
            <FlagBox label="Corporate ID join" value="Disabled" tone="slate" />
            <FlagBox
              label="Auto-join on Corporate ID"
              value="Planned"
              tone="slate"
            />
            <FlagBox
              label="Subscription billing"
              value="Stripe Subs (planned)"
              tone="slate"
            />
          </div>

          <div className="mt-4 flex items-start gap-2 text-xs text-amber-800 bg-amber-100/60 rounded-md p-3 border border-amber-200">
            <Sparkles className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <p>
              When ready: Phase A ships registration paths + Corporate ID
              creation/join + companies collection + <code>is_corporate</code>{' '}
              exemption. Phase B layers Stripe Subscriptions + Company Admin
              Console + seat-limit enforcement.
            </p>
          </div>
        </section>

        {/* ───────────────────────────── Test-mode override ───────────────────────────── */}
        <section className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="h-4 w-4 text-slate-500" />
            <h3 className="font-semibold text-slate-800 text-sm">
              QA / Test mode flags
            </h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
            <FlagBox
              label="force_credit_charging"
              value="Per-user opt-in"
              tone="cyan"
            />
            <FlagBox
              label="Demo mode"
              value="Read-only"
              tone="slate"
            />
            <FlagBox
              label="Admin exemption"
              value="is_admin → free"
              tone="slate"
            />
          </div>
          <p className="text-xs text-slate-500 mt-3">
            <code>force_credit_charging</code> is a per-user flag that lets an
            otherwise-exempt admin/unlimited user experience the credit flow
            for QA. Toggle in MongoDB on the user document.
          </p>
        </section>
      </CardContent>
    </Card>
  );
}

function FlagBox({ label, value, tone }) {
  const tones = {
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    amber: 'border-amber-200 bg-amber-50 text-amber-800',
    cyan: 'border-cyan-200 bg-cyan-50 text-cyan-800',
    slate: 'border-slate-200 bg-slate-50 text-slate-700',
  };
  return (
    <div className={`rounded-md border px-3 py-2 ${tones[tone] || tones.slate}`}>
      <p className="text-[10px] uppercase tracking-wide opacity-70">{label}</p>
      <p className="font-semibold">{value}</p>
    </div>
  );
}
